import type { FormEvent } from 'react';
import { useRef } from 'react';
import { useTypedAppForm } from '@gonfalon/forms-experimental/hooks';
import { MarketoForm } from '@gonfalon/marketing-forms/forms';
import { useMarketoForm } from '@gonfalon/marketo/useMarketoForm';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, Button, Form } from '@launchpad-ui/components';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { trackSignupEnterpriseDemoFormSubmitted } from '../analytics';

import styles from './EnterpriseDemoSignupForm.module.css';
import signUpFormStyles from './signUpFormLayout.module.css';

export const MARKETO_SUBMIT_TIMEOUT_MS = 10_000;

const SUBMIT_ERROR_MESSAGE =
  'Something went wrong submitting your request. Try again, or email trials@launchdarkly.com and our team will be in touch.';

const requiredField = z.string().min(1, { error: () => msg('onboarding.enterpriseDemoFormLegacy.requiredError') });

const FormSchema = z.object({
  firstName: requiredField,
  lastName: requiredField,
  company: requiredField,
  jobTitle: requiredField,
  workEmail: requiredField.email({ error: () => msg('onboarding.enterpriseDemoFormLegacy.emailError') }),
});

type FormData = z.infer<typeof FormSchema>;

export const EnterpriseDemoSignupFormLegacy = () => {
  const marketoSubmitPromiseRef = useRef<{
    resolve: () => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  } | null>(null);

  const {
    scriptLoaded,
    formReady,
    onSubmit: marketoSubmit,
  } = useMarketoForm({
    formId: MarketoForm.EnterpriseDemo,
    onSuccess: () => {
      marketoSubmitPromiseRef.current?.resolve();
    },
  });

  const waitForMarketoSuccess = () =>
    new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        if (!marketoSubmitPromiseRef.current) {
          return;
        }

        marketoSubmitPromiseRef.current = null;
        reject(new Error(SUBMIT_ERROR_MESSAGE));
      }, MARKETO_SUBMIT_TIMEOUT_MS);

      marketoSubmitPromiseRef.current = {
        timeoutId,
        resolve: () => {
          if (!marketoSubmitPromiseRef.current) {
            return;
          }

          clearTimeout(marketoSubmitPromiseRef.current.timeoutId);
          marketoSubmitPromiseRef.current = null;
          resolve();
        },
        reject: (error: Error) => {
          if (!marketoSubmitPromiseRef.current) {
            return;
          }

          clearTimeout(marketoSubmitPromiseRef.current.timeoutId);
          marketoSubmitPromiseRef.current = null;
          reject(error);
        },
      };
    });

  const submitDemo = useMutation({
    mutationFn: async (value: FormData) => {
      if (!scriptLoaded || !formReady) {
        throw new Error(SUBMIT_ERROR_MESSAGE);
      }

      const marketoSuccessPromise = waitForMarketoSuccess();

      const started = marketoSubmit({
        Email: value.workEmail,
        Company: value.company,
        FirstName: value.firstName,
        LastName: value.lastName,
        Title: value.jobTitle,
      });

      if (!started) {
        const pending = marketoSubmitPromiseRef.current;
        marketoSubmitPromiseRef.current = null;
        if (pending) {
          clearTimeout(pending.timeoutId);
        }
        throw new Error(SUBMIT_ERROR_MESSAGE);
      }

      await marketoSuccessPromise;
      trackSignupEnterpriseDemoFormSubmitted();
    },
  });

  const form = useTypedAppForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      company: '',
      jobTitle: '',
      workEmail: '',
    },
    validators: {
      onChange: FormSchema,
      onSubmit: FormSchema,
      onMount: FormSchema,
    },
    onSubmit: ({ value }) => {
      submitDemo.mutate(value);
    },
  });

  const { AppForm, AppField, Subscribe } = form;

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    void form.handleSubmit();
  };

  const marketoReady = scriptLoaded && formReady;

  if (submitDemo.isSuccess) {
    return (
      <Alert status="success" className={styles.successMessage}>
        <Message id="onboarding.enterpriseDemoFormLegacy.successBody" />
      </Alert>
    );
  }

  return (
    <Form onSubmit={handleFormSubmit} className={signUpFormStyles.form}>
      <AppForm>
        {submitDemo.isError && (
          <Alert status="error">
            {submitDemo.error instanceof Error ? submitDemo.error.message : SUBMIT_ERROR_MESSAGE}
          </Alert>
        )}
        <div className={signUpFormStyles.name}>
          <div>
            <AppField name="firstName">
              {(field) => (
                <field.TextInputField label={msg('onboarding.enterpriseDemoFormLegacy.firstNameLabel')} isRequired />
              )}
            </AppField>
          </div>
          <div>
            <AppField name="lastName">
              {(field) => (
                <field.TextInputField label={msg('onboarding.enterpriseDemoFormLegacy.lastNameLabel')} isRequired />
              )}
            </AppField>
          </div>
        </div>
        <div className={signUpFormStyles.username}>
          <AppField name="company">
            {(field) => (
              <field.TextInputField label={msg('onboarding.enterpriseDemoFormLegacy.companyLabel')} isRequired />
            )}
          </AppField>
        </div>
        <div className={signUpFormStyles.username}>
          <AppField name="jobTitle">
            {(field) => (
              <field.TextInputField label={msg('onboarding.enterpriseDemoFormLegacy.jobTitleLabel')} isRequired />
            )}
          </AppField>
        </div>
        <div className={signUpFormStyles.username}>
          <AppField name="workEmail">
            {(field) => (
              <field.TextInputField
                label={msg('onboarding.enterpriseDemoFormLegacy.emailLabel')}
                type="email"
                isRequired
              />
            )}
          </AppField>
        </div>
        <div className={signUpFormStyles.submit}>
          <Subscribe selector={(state) => state.isValid}>
            {(isValid) => (
              <Button
                variant="primary"
                isDisabled={!isValid || !marketoReady || submitDemo.isPending}
                className={signUpFormStyles.submitButton}
                type="submit"
                isPending={submitDemo.isPending}
              >
                {submitDemo.isPending
                  ? msg('onboarding.enterpriseDemoFormLegacy.submitting')
                  : msg('onboarding.enterpriseDemoFormLegacy.submit')}
              </Button>
            )}
          </Subscribe>
        </div>
      </AppForm>
    </Form>
  );
};
